(() => {
  const storageKey = 'budetz-recently-viewed-v1';
  const maximumStored = 12;

  const readHandles = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
      return Array.isArray(parsed) ? parsed.filter((handle) => typeof handle === 'string') : [];
    } catch (_error) {
      return [];
    }
  };

  const writeHandles = (handles) => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(handles.slice(0, maximumStored)));
    } catch (_error) {
      // Recently viewed stays optional when storage is blocked.
    }
  };

  const trackCurrentProduct = () => {
    const data = document.getElementById('BudetzCurrentProduct');
    if (!data) return;

    try {
      const product = JSON.parse(data.textContent);
      if (!product.handle) return;
      writeHandles([product.handle, ...readHandles().filter((handle) => handle !== product.handle)]);
    } catch (_error) {
      // Invalid or unavailable product data should never block the storefront.
    }
  };

  const formatMoney = (amount, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'USD',
      }).format(Number(amount || 0) / 100);
    } catch (_error) {
      return `${(Number(amount || 0) / 100).toFixed(2)} ${currency || ''}`.trim();
    }
  };

  const createRecentCard = (product, index, currency) => {
    const card = document.createElement('article');
    card.className = 'budetz-recent-card budetz-reveal';
    card.setAttribute('role', 'listitem');

    const productUrl = `${window.Shopify?.routes?.root || '/'}products/${product.handle}`;
    const mediaLink = document.createElement('a');
    mediaLink.className = 'budetz-recent-card__media';
    mediaLink.href = productUrl;
    mediaLink.setAttribute('aria-label', product.title);

    const imageUrl = product.featured_image || product.images?.[0];
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = `${imageUrl}${String(imageUrl).includes('?') ? '&' : '?'}width=800`;
      image.alt = product.title;
      image.loading = 'lazy';
      image.width = 800;
      image.height = 1000;
      mediaLink.append(image);
    }

    const number = document.createElement('span');
    number.className = 'budetz-recent-card__index';
    number.textContent = String(index + 1).padStart(2, '0');
    mediaLink.append(number);

    const content = document.createElement('div');
    content.className = 'budetz-recent-card__content';
    const titleLink = document.createElement('a');
    titleLink.href = productUrl;
    const title = document.createElement('h3');
    title.textContent = product.title;
    titleLink.append(title);
    content.append(titleLink);

    const price = document.createElement('div');
    price.className = 'budetz-recent-card__price';
    const currentPrice = document.createElement('span');
    currentPrice.textContent = formatMoney(product.price, currency);
    price.append(currentPrice);
    if (product.compare_at_price && product.compare_at_price > product.price) {
      const comparePrice = document.createElement('span');
      comparePrice.className = 'budetz-recent-card__compare';
      comparePrice.textContent = formatMoney(product.compare_at_price, currency);
      price.append(comparePrice);
    }
    content.append(price);
    card.append(mediaLink, content);
    return card;
  };

  const renderRecentlyViewed = async (root) => {
    if (root.dataset.budetzReady === 'true') return;
    root.dataset.budetzReady = 'true';

    const currentHandle = root.dataset.currentHandle;
    const limit = Number(root.dataset.limit || 4);
    const handles = readHandles().filter((handle) => handle !== currentHandle).slice(0, limit);
    if (!handles.length) return;

    const routeRoot = window.Shopify?.routes?.root || '/';
    const products = (
      await Promise.all(
        handles.map(async (handle) => {
          try {
            const response = await fetch(`${routeRoot}products/${encodeURIComponent(handle)}.js`, {
              headers: { Accept: 'application/json' },
            });
            return response.ok ? response.json() : null;
          } catch (_error) {
            return null;
          }
        })
      )
    ).filter(Boolean);

    if (!products.length) return;
    const track = root.querySelector('[data-budetz-recent-track]');
    products.forEach((product, index) => track.append(createRecentCard(product, index, root.dataset.currency)));
    root.hidden = false;
    requestAnimationFrame(() => observeReveals(root));
  };

  const observeVideos = (scope = document) => {
    const videos = scope.querySelectorAll('.budetz-video-card__video:not([data-budetz-video-ready])');
    if (!videos.length) return;

    videos.forEach((video) => {
      video.dataset.budetzVideoReady = 'true';
      video.muted = true;
      video.setAttribute('playsinline', '');
      const button = video.closest('.budetz-video-card__media')?.querySelector('[data-budetz-video-toggle]');
      button?.addEventListener('click', () => {
        const shouldPlay = video.paused;
        video.dataset.budetzUserPaused = shouldPlay ? 'false' : 'true';
        const label = button.querySelector('[data-budetz-video-toggle-label]');
        if (shouldPlay) {
          video.play().catch(() => {});
          button.setAttribute('aria-label', 'Pause video');
          button.setAttribute('aria-pressed', 'false');
          if (label) label.textContent = 'PAUSE';
        } else {
          video.pause();
          button.setAttribute('aria-label', 'Play video');
          button.setAttribute('aria-pressed', 'true');
          if (label) label.textContent = 'PLAY';
        }
      });
    });

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      videos.forEach((video) => {
        video.pause();
        video.dataset.budetzUserPaused = 'true';
        const button = video.closest('.budetz-video-card__media')?.querySelector('[data-budetz-video-toggle]');
        const label = button?.querySelector('[data-budetz-video-toggle-label]');
        button?.setAttribute('aria-label', 'Play video');
        button?.setAttribute('aria-pressed', 'true');
        if (label) label.textContent = 'PLAY';
      });
      return;
    }

    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.target.dataset.budetzUserPaused !== 'true') {
            entry.target.play().catch(() => {});
          } else {
            entry.target.pause();
          }
        });
      },
      { threshold: 0.35 }
    );
    videos.forEach((video) => observer.observe(video));
  };

  const observeReveals = (scope = document) => {
    const items = scope.querySelectorAll('.budetz-reveal:not([data-budetz-reveal-ready])');
    if (!items.length) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    items.forEach((item, index) => {
      item.dataset.budetzRevealReady = 'true';
      item.style.transitionDelay = `${Math.min(index % 4, 3) * 70}ms`;
      observer.observe(item);
    });
  };

  const addEditorialRevealTargets = (scope = document) => {
    scope
      .querySelectorAll('.budetz-look-card, .collection .grid__item, .budetz-editorial-statement__index, .budetz-editorial-statement__copy')
      .forEach((item) => item.classList.add('budetz-reveal'));
  };

  const initializeProductGalleryDots = (scope = document) => {
    scope.querySelectorAll('[data-budetz-media-dots]:not([data-budetz-dots-ready])').forEach((dots) => {
      dots.dataset.budetzDotsReady = 'true';
      const sliderComponent = dots.closest('slider-component');
      const slider = sliderComponent?.querySelector('.product__media-list');
      const slides = slider ? Array.from(slider.querySelectorAll('.product__media-item')) : [];
      const buttons = Array.from(dots.querySelectorAll('[data-budetz-media-dot]'));
      if (!slider || !slides.length || !buttons.length) return;

      const setActiveDot = (index) => {
        buttons.forEach((button, buttonIndex) => {
          const active = buttonIndex === index;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-current', active ? 'true' : 'false');
        });
      };

      let frame;
      const updateFromScroll = () => {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => {
          const current = slides.reduce(
            (closest, slide, index) => {
              const distance = Math.abs(slider.scrollLeft - (slide.offsetLeft - slider.offsetLeft));
              return distance < closest.distance ? { index, distance } : closest;
            },
            { index: 0, distance: Number.POSITIVE_INFINITY }
          );
          setActiveDot(current.index);
        });
      };

      buttons.forEach((button, index) => {
        button.addEventListener('click', () => {
          const slide = slides[index];
          if (!slide) return;
          slider.scrollTo({
            left: slide.offsetLeft - slider.offsetLeft,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          });
          setActiveDot(index);
        });
      });
      slider.addEventListener('scroll', updateFromScroll, { passive: true });
      setActiveDot(0);
    });
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-budetz-recently-viewed]').forEach(renderRecentlyViewed);
    observeVideos(scope);
    addEditorialRevealTargets(scope);
    initializeProductGalleryDots(scope);
    observeReveals(scope);
  };

  document.documentElement.classList.add('budetz-motion-ready');
  trackCurrentProduct();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    event.target.querySelector('video')?.play().catch(() => {});
  });
})();
